import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, RefreshCw, AlertTriangle, Filter } from 'lucide-react';
import { adminService } from '../services/adminService';
import { formatDateTime, formatNumber } from '../utils/format';
import './AdminPage.css';

const Audit: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const [type, setType] = useState<string>('');
  const [actorId, setActorId] = useState<string>('');
  const [since, setSince] = useState<string>('');
  const [limit, setLimit] = useState<number>(100);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await adminService.getAuditEvents({
        limit,
        type: type || undefined,
        since: since || undefined,
        actor_id: actorId ? Number(actorId) : undefined,
      });
      setItems(res?.items || []);
      setTotal(res?.total || 0);
      setNote(res?.note || null);
    } catch (e) {
      console.warn('audit load failed', e);
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [type, actorId, since, limit]);

  useEffect(() => { load(); }, [load]);

  const eventTypes = useMemo(() => {
    const set = new Set<string>();
    items.forEach((i: any) => i.event_type && set.add(i.event_type));
    return Array.from(set).sort();
  }, [items]);

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Activity size={20} style={{ marginRight: 8 }} />Audit Logs</h1>
          <p className="page-subtitle">Recent admin, security and compliance events</p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={load} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {note && (
        <div className="panel">
          <div className="panel-body" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <AlertTriangle size={16} color="#f59e0b" />
            <span style={{ fontSize: '0.85rem', color: '#92400e' }}>{note}</span>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-header">
          <h3><Filter size={16} /> Filters</h3>
          <span className="kpi-detail">{formatNumber(items.length)} of {formatNumber(total)} events shown</span>
        </div>
        <div className="panel-body">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input placeholder="Event type" value={type} onChange={(e) => setType(e.target.value)} list="event-types" />
            <datalist id="event-types">
              {eventTypes.map((t) => <option key={t} value={t} />)}
            </datalist>
            <input placeholder="Actor (user) ID" type="number" value={actorId} onChange={(e) => setActorId(e.target.value)} />
            <input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} />
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-body tight">
          {loading ? <div className="skeleton" style={{ height: 240, margin: 16 }} /> : items.length === 0 ? (
            <div className="empty">No audit events match these filters</div>
          ) : (
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>When</th><th>Type</th><th>Actor</th>
                    <th>Target</th><th>IP</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((ev: any) => (
                    <tr key={ev.id} onClick={() => setSelected(ev)} style={{ cursor: 'pointer' }}>
                      <td>{formatDateTime(ev.created_at)}</td>
                      <td><span className="pill">{ev.event_type}</span></td>
                      <td>{ev.actor_type ? `${ev.actor_type}#${ev.actor_id ?? '—'}` : ev.actor_id ?? '—'}</td>
                      <td>{ev.target_type ? `${ev.target_type}#${ev.target_id ?? '—'}` : '—'}</td>
                      <td>{ev.ip_address || '—'}</td>
                      <td style={{ color: '#410C6D', fontSize: 12 }}>view →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="panel">
          <div className="panel-header">
            <h3>Event detail · #{selected.id}</h3>
            <button className="btn secondary" onClick={() => setSelected(null)}>Close</button>
          </div>
          <div className="panel-body">
            <pre style={{ background: '#f8fafc', padding: 12, borderRadius: 8, overflow: 'auto', fontSize: 12 }}>
              {JSON.stringify(selected, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default Audit;
